import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { useUser } from '@taki/auth';
import type { Environnement, TakiSdk } from '@taki/sdk';
import { AppRuntimeProvider } from '@taki/app-runtime';
import { titreSansPeriode } from '@taki/data-model';
import { TakiShell } from '@taki/shell';
import {
  AccesProvider,
  accesMembreIndisponible,
  autorisationDeMembre,
  BrandingProvider,
  IaSettingsProvider,
  InstallationProvider,
  JournalProvider,
  NotificationsProvider,
  LiaConversationsProvider,
  ConversationsCentreProvider,
  Button,
  CreationProvider,
  DeclarationsProvider,
  LiensProvider,
  ModelesProvider,
  PersonnesProvider,
  RecurrencesProvider,
  filtrerModelesParType,
  invaliderAnnuaires,
  niveauEffectif,
  niveauParDefaut,
  patchDeRattachement,
} from '@taki/ui';
import type {
  AccesPort,
  CreationPort,
  FicheMembre,
  LiensPort,
  MembresPort,
  MembreVue,
  ModelesPort,
  NiveauAcces,
  PersonnesPort,
  PersonneVue,
  RecurrencesPort,
  RoleOrg,
} from '@taki/ui';
import { useBrandingDeLHote } from './branding.js';
import { useInstallationDeLHote } from './installation.js';
import { useDeclarationsDeLHote } from './declarations.js';
import { useIaSettingsDeLHote } from './iaSettings.js';
import { useJournalDeLHote } from './journal.js';
import { useNotificationsDeLHote } from './notifications.js';
import { useLiaConversationsDeLHote } from './liaConversations.js';
import { useConversationsCentreDeLHote } from './conversationsCentre.js';
import { demoRegistry } from './registry.js';

/** Les quatre roles d'org que le socle connait (`orgRoleSchema`). */
const ROLES: readonly RoleOrg[] = ['owner', 'admin', 'member', 'viewer'];

/** Un role venu des claims n'est qu'une chaine : on ne garde que les quatre vrais. */
function roleValide(valeur: string | undefined): RoleOrg | null {
  return valeur != null && (ROLES as readonly string[]).includes(valeur)
    ? (valeur as RoleOrg)
    : null;
}

/**
 * Contexte applicatif : expose le SDK et la config aux ecrans.
 *
 * La "cage IA" impose que les ecrans n'accedent JAMAIS a Firebase directement :
 * tout passe par `@taki/sdk`. On partage donc l'instance unique du SDK
 * (initialisee dans main.tsx) via ce contexte, plutot que de la reconstruire.
 */
interface TakiAppContextValue {
  config: TakiConfig;
  sdk: TakiSdk;
}

const TakiAppContext = createContext<TakiAppContextValue | null>(null);

/** Accede au SDK + config ; leve si utilise hors du provider <App>. */
export function useTakiApp(): TakiAppContextValue {
  const ctx = useContext(TakiAppContext);
  if (!ctx) {
    throw new Error("useTakiApp doit etre utilise a l'interieur de <App>.");
  }
  return ctx;
}

export interface AppProps {
  config: TakiConfig;
  sdk: TakiSdk;
  /** Lu par `main.tsx` avant la connexion (la bande STAGING), `undefined` tant qu'il n'est pas lu. */
  environnement: Environnement | undefined;
}

/**
 * Racine de l'app `hello`.
 *
 * - Fournit le SDK + la config aux ecrans via le contexte applicatif (la cage
 *   IA : ContactsScreen accede au SDK via `useTakiApp`, jamais Firebase direct).
 * - Monte le `TakiShell` : c'est LUI qui fournit tout le chrome (rail de nav,
 *   compte, auth guard, theme du branding) et monte les apps du registre.
 * - Fournit le PORT DES MODELES DE FICHES au socle (`ModelesProvider`) : les
 *   deux gestes standard (« Enregistrer comme modele », la fleche de « Ajouter »)
 *   vivent dans `@taki/ui`, qui ne peut pas parler au SDK ; l'hote branche donc
 *   `sdk.templates` sur la forme que le socle attend. Un modele emporte aussi ses
 *   FICHES LIEES (`children`) : elles traversent ce port telles quelles, a
 *   l'enregistrement comme a la modification, et le mode Modeles les ecrit dans
 *   le document du modele, jamais dans une collection.
 * - Fournit le PORT DES LIENS ENTRE PORTES (`LiensProvider`), pour la meme
 *   raison : une carte liee lit les fiches d'une AUTRE porte, et c'est l'hote
 *   qui sait passer d'un nom de famille au repository (`sdk.repositoryOf`).
 * - Fournit le PORT DE CREATION (`CreationProvider`), toujours pour la meme
 *   raison : depuis le 09/09/2026 « + Ajouter » ouvre une FICHE VIDE que le
 *   socle tient, et c'est l'hote qui la fait naitre (`sdk.createIn`).
 * - Fournit le RUNTIME DES APPS DECLAREES (`AppRuntimeProvider`), meme raison
 *   encore : une app declaree n'a pas d'ecran a elle, c'est l'ecran generique de
 *   `@taki/app-runtime` qui interprete ses fichiers, et il a besoin du SDK.
 * - Fournit le PORT DE BRANDING (`BrandingProvider`), toujours pour la meme
 *   raison : le nom, le logo et la couleur d'accent que le client choisit
 *   vivent dans un document Firestore, et c'est l'hote qui l'observe
 *   (`sdk.branding`) puis le fusionne avec la config (`branding.ts`).
 * - Fournit le PORT DES PERSONNES (`PersonnesProvider`), meme raison : un champ
 *   `personne` stocke un `personId`, et c'est l'hote qui lit l'annuaire
 *   (`sdk.personnes`) pour que le socle montre un NOM, et qui sait qui est
 *   connecte (le jeton `socle:personneConnectee` d'une fiche de creation).
 *
 * Le contexte SDK enveloppe le shell pour rester disponible dans les ecrans
 * d'apps (ContactsScreen) montes a l'interieur des routes du shell.
 */
export function App({ config, sdk, environnement }: AppProps): JSX.Element {
  /*
   * LE PORT DES MODELES DE FICHES, monte ICI et pas dans le shell : c'est l'HOTE
   * qui a le SDK (le shell ne l'a pas dans ses dependances, et c'est voulu).
   * Le socle, lui, ne connait que la FORME du service : il n'importe jamais
   * `@taki/sdk` (ordre de build sdk -> ui -> shell -> apps).
   *
   * CONSTRUIT UNE FOIS (`useMemo`) : le port descend par contexte jusqu'au
   * chassis, qui LIT la liste des modeles dans un effet dependant du port. Un
   * objet neuf a chaque rendu relancerait cette lecture a chaque frappe de
   * recherche.
   */
  const modeles = useMemo<ModelesPort>(
    () => ({
      /* LE SDK FILTRE LA FAMILLE, LE SOCLE FILTRE LE SOUS-TYPE. Le `type` d'un
         modele est sa famille CIBLE (la clef du repository, ex. `assets`) : elle
         se filtre au serveur. Le sous-type de la porte (`materiel`) vit dans
         `targetType`, qui n'est pas une clef du repository : il se trie apres
         lecture, par la fonction pure du socle. Sans ce second tri, deux apps
         qui rangent leurs fiches dans la meme famille verraient les modeles
         l'une de l'autre. */
      list: async (family, type) =>
        filtrerModelesParType(await sdk.templates.list({ type: family, orderBy: 'name' }), type),
      /* UN MODELE PAR SON ID, ET C'EST L'ADRESSE D'UN ENFANT QUI L'EXIGE
         (`s=interventions&id=<modeleId>/<enfantId>`) : la porte liee ne liste
         pas les modeles du materiel, donc un F5 sur cette adresse n'a aucune
         liste ou retrouver le parent. Rien a traduire : `sdk.templates.get`
         rend deja un `Template` complet, `children` compris, ou `null`. */
      get: async (templateId) => await sdk.templates.get(templateId),
      /* `type` N'EST PAS REPASSE AU SDK, et ce n'est pas un oubli : c'est lui qui
         lit le sous-type SUR la fiche pour poser `targetType`. Le lui redonner
         ouvrirait la porte a un modele range sous un type que la fiche ne porte
         pas.

         `children` TRAVERSE TEL QUEL QUAND IL EST LA, et son ABSENCE se relaie
         comme une absence (`...(children === undefined ? {} : { children })`) :
         le socle distingue « aucune lecture n'a eu lieu » (une porte sans lien,
         un brouillon, pas de port des liens) de « aucune fiche liee », et poser
         `children: undefined` effacerait cette distinction sous
         `exactOptionalPropertyTypes`. Le SDK fait le reste : il retire de chaque
         enfant les champs standard et le champ du lien, et pose un id par
         enfant (`templateChildFromRecord`, `avecIdsDEnfants`). */
      saveFromRecord: async ({ family, record, name, children }) =>
        await sdk.templates.fromRecord({
          family,
          record,
          name,
          ...(children === undefined ? {} : { children }),
        }),
      /* « Creer », SUR LA FICHE OUVERTE DEPUIS UN MODELE (23/09/2026) : la fiche
         ENTIERE du brouillon part en overrides, `type` en tete comme pour
         `creation.create`. Les overrides passent APRES les champs du modele
         (`planInstantiation`) : une case videe a l'ecran ne revient donc pas, et
         un `type` parasite dans les champs du modele perd contre celui de la
         porte. Le SDK relit le modele, cree la racine puis ses enfants.

         UN `type` VIDE NE PART PAS : une porte sans sous-type l'envoie vide, et
         le SDK retire les chaines vides APRES les overrides. Pose, il effacerait
         le `targetType` du modele au lieu de le laisser gagner. */
      createFrom: async ({ templateId, type, fields }) =>
        await sdk.templates.instantiate(templateId, {
          ...(type === '' ? {} : { type }),
          ...fields,
        }),
      archive: async (templateId) => {
        await sdk.templates.archive(templateId);
      },
      /* LE PATCH TRAVERSE TEL QUEL, et c'est le seul verbe du port pour lequel
         l'hote n'a rien a traduire : `TemplateUpdateInputSchema` accepte
         exactement les quatre clefs que le socle envoie (`name`, `description`,
         `fields` et, depuis le chantier `modeles-enfants`, `children`), toutes
         optionnelles, et le SDK valide avant d'ecrire. Le socle refusionne
         `fields` ET `children` EN ENTIER avant d'appeler (contrat du port) : on
         ne fabrique donc aucun delta ici, et une clef ABSENTE laisse la valeur
         en place la ou un tableau VIDE retire le dernier enfant. */
      update: async (templateId, patch) => {
        await sdk.templates.update(templateId, patch);
      },
      /* CREER UN MODELE NU, depuis le « + Ajouter » du mode Modeles. Les deux
         noms ne se recouvrent PAS entre le socle et le SDK, et c'est la seule
         chose que cette ligne fait : la `family` du socle (le repository, ex.
         `assets`) est le `type` du SDK, et le `type` du socle (le sous-type de
         la porte, ex. `materiel`) est son `targetType`. C'est deja la lecture
         que fait `list` juste au-dessus, dans l'autre sens.

         `fields: {}` PARCE QU'UN MODELE NAIT VIDE, et que le schema l'exige :
         on part d'un modele nu et on remplit ses cases a l'ecran, dans le mode,
         comme une fiche (le moteur route ces ecritures vers `update`).

         `orgId` VIENT DE LA CONFIG, comme pour `members.create` plus bas : le
         SDK ne le devine pas, c'est l'hote qui sait dans quelle org il tourne. */
      create: async ({ family, type, name, fields }) => {
        /* `fields` VIENT DE LA FICHE VIDE (pivot du 10/09/2026) : « + Ajouter »
           en mode Modeles ouvre un BROUILLON, le titre saisi devient `name` et
           les autres cases deviennent ces champs (`modeleDuBrouillon`). Le repli
           `{}` reste : le schema exige la clef, et un modele nu est un etat
           legitime (aucune case remplie). */
        const cree = await sdk.templates.create({
          orgId: config.orgId,
          type: family,
          targetType: type,
          name,
          fields: fields ?? {},
        });
        /* L'ID REMONTE : le socle OUVRE le modele qu'il vient de creer, comme il
           ouvre la fiche qu'il vient de creer. Le reconnaitre a son nom dans la
           liste relue ouvrirait le mauvais des deux le jour ou deux modeles
           portent le meme. */
        return { id: cree.id };
      },
    }),
    [sdk, config.orgId],
  );

  /*
   * LE PORT DES PLANS RECURRENTS, a cote de celui des modeles et pour la meme
   * raison : l'hote a le SDK, le socle ne connait que la FORME du service. Rien
   * a traduire : un plan ne porte que son rythme et cite un modele
   * (`templateId`). Depuis une fiche, le SDK tire d'abord ce modele puis cree le
   * plan dans le meme lot que l'inscription du plan sur le modele ; depuis un
   * modele, il cree le plan seul (`sdk.recurrences`).
   */
  const recurrences = useMemo<RecurrencesPort>(
    () => ({
      nomPropose: titreSansPeriode,
      fromRecord: async ({ family, type, record, name, children }) => {
        const plan = await sdk.recurrences.fromRecord({
          family,
          ...(type === '' ? {} : { type }),
          record,
          name,
          ...(children === undefined ? {} : { children }),
        });
        return { id: plan.id };
      },
      fromTemplate: async ({ templateId, name }) => {
        const plan = await sdk.recurrences.fromTemplate({ templateId, name });
        return { id: plan.id };
      },
    }),
    [sdk],
  );

  /*
   * LE PORT DES LIENS ENTRE PORTES, monte ICI pour la meme raison que les
   * modeles : c'est l'HOTE qui a le SDK, le socle ne connait que la FORME du
   * service.
   *
   * IL NE TRADUIT RIEN, ET C'EST VOULU : les quatre verbes de LECTURE du socle
   * sont ceux de `FamilyRepository`, au nom pres, parce que les deux ont ete
   * ecrits pour le meme geste (« les interventions de CE materiel »).
   * `repositoryOf` fait le seul vrai travail : passer d'un NOM de famille (une
   * chaine, connue a l'execution) au repository type, en levant sur une famille
   * inconnue, ou sur une famille sans la surface commune.
   *
   * LE CINQUIEME VERBE, `rattacher`, EST LE SEUL QUI ECRIVE, et il relaie lui
   * aussi : `FamilyRepository` promet `update` depuis qu'une carte liee ecrit,
   * et c'est `repositoryOf` qui verifie le verbe avant de le rendre.
   *
   * AUCUN `orderBy` NE PASSE, parce que le socle n'en demande pas : une carte
   * liee trie CHEZ ELLE, sur la fenetre chargee. Un tri serveur sur un champ
   * facultatif ferait exclure par Firestore les fiches qui ne le portent pas,
   * et la fenetre ne couvrirait plus le meme jeu que le compte
   * (`ArgumentsDeListe`, `@taki/ui`).
   *
   * CONSTRUIT UNE FOIS (`useMemo`) : chaque carte liee lit dans un effet qui
   * depend du port, un objet neuf a chaque rendu rechargerait sans fin.
   */
  const liens = useMemo<LiensPort>(
    () => ({
      /* `archived` TRAVERSE TEL QUEL : le socle emploie deja le vocabulaire du
         SDK (`'exclude'` / `'all'`), donc l'hote relaie sans traduire. Une
         table de correspondance ici aurait fini par inverser un sens. Absent,
         le SDK exclut les archives, ce que demande une carte liee ; l'annuaire,
         lui, demande `'all'` pour pouvoir NOMMER une fiche archivee. */
      list: async (family, { type, where, limit, archived }) =>
        await sdk.repositoryOf(family).list({
          where,
          limit,
          ...(type === undefined ? {} : { type }),
          ...(archived === undefined ? {} : { archived }),
        }),
      /* LES TROIS AGREGATS RELAIENT `archived` COMME LA LISTE, et par le meme
         non-traducteur : le socle emploie deja le vocabulaire du SDK. Absent,
         le SDK compte les fiches ACTIVES (le compte d'un rollup, le pied d'un
         tableau) ; `'all'` est demande par le seul refus de suppression, qui
         compte ce qui reste ACCROCHE a une fiche, archives comprises. Les
         supprimes, eux, sont sortis au SERVEUR par le SDK sur ce mode. */
      count: async (family, { type, where, archived }) =>
        await sdk.repositoryOf(family).count({
          where,
          ...(type === undefined ? {} : { type }),
          ...(archived === undefined ? {} : { archived }),
        }),
      sum: async (family, field, { type, where, archived }) =>
        await sdk.repositoryOf(family).sum(field, {
          where,
          ...(type === undefined ? {} : { type }),
          ...(archived === undefined ? {} : { archived }),
        }),
      average: async (family, field, { type, where, archived }) =>
        await sdk.repositoryOf(family).average(field, {
          where,
          ...(type === undefined ? {} : { type }),
          ...(archived === undefined ? {} : { archived }),
        }),
      /* LES FICHES QU'UN TOTAL PONDERE DESIGNE (`RollupFieldDef.times`) : une
         lecture par id DISTINCT (le socle a deja dedoublonne), jamais une par
         ligne qui le cite, par paquets pour ne pas ouvrir mille requetes d'un
         coup. Un `get` est cadre par l'org par le CHEMIN du document
         (`orgs/{orgId}/<famille>/{id}`), et un id inconnu rend `null` : il
         disparait du resultat, le socle le compte pour une ligne sans fiche.
         Le SDK n'offre pas de lecture `in` sur les ids (limite connue,
         `docs/socle/total-pondere.md`). */
      lireParIds: async (family, ids) => {
        const depot = sdk.famille(family);
        const lues: Array<Record<string, unknown>> = [];
        for (let debut = 0; debut < ids.length; debut += 25) {
          const paquet = await Promise.all(ids.slice(debut, debut + 25).map((id) => depot.get(id)));
          for (const fiche of paquet) if (fiche !== null) lues.push(fiche);
        }
        return lues;
      },
      /* LE SEUL VERBE D'ECRITURE DU PORT, ET IL ECRIT SUR L'ENFANT : « Lier une
         fiche existante » pose l'id de la fiche OUVERTE sur la fiche CHOISIE,
         au champ du lien. La fiche ouverte ne bouge pas : une reference vit
         dans le document de celui qui designe.

         LE PATCH VIENT DU SOCLE (`patchDeRattachement`) et l'hote n'y touche
         pas : c'est lui qui sait si le chemin est natif (`productId`, a la
         racine) ou sur-mesure (`customFields.fournisseurId`, dans le sac).
         Ecrire la clef nue d'un champ du sac passerait la validation (le sac
         est libre) et rendrait la fiche invisible de toutes les cartes liees,
         qui cadrent sur `customFields.fournisseurId`.

         LE SDK FUSIONNE LE SAC CLEF PAR CLEF (`buildUpdateData` eclate
         `customFields` en chemins pointes) : les autres champs sur-mesure de la
         fiche rattachee survivent. C'est la meme regle que `CreationPort`
         au-dessous, dans l'autre sens : la creation POSE les valeurs imposees
         dans l'entree, le rattachement les PATCHE sur une fiche qui existe. */
      rattacher: (family, id, chemin, valeur) =>
        sdk.repositoryOf(family).update(id, patchDeRattachement(chemin, valeur)),
    }),
    [sdk],
  );

  /*
   * LE PORT DE CREATION, monte ICI pour la meme raison que les deux precedents :
   * l'hote a le SDK, le socle ne l'a pas. Le socle ne connait que la FORME
   * (`CreationPort`) : une famille designee par son NOM, un sous-type, et les
   * champs que le brouillon a ranges.
   *
   * L'HOTE POSE `orgId` ET `type`, ET C'EST LUI QUI DOIT LE FAIRE. `orgId`
   * cadre l'isolation entre clients : le socle ne sait pas dans quelle org il
   * tourne, c'est la config chargee au runtime qui le dit, et une fiche creee
   * sans lui serait refusee par les rules (regle d'or 1). `type`, lui, vient de
   * la porte et voyage a PART dans le port, parce que le brouillon ne le range
   * pas dans ses champs : le remettre ici est le seul endroit ou les deux se
   * rejoignent. Les champs passent APRES, mais aucun ne s'appelle `orgId` ni
   * `type` (`champsDuBrouillon` retire deja le second).
   *
   * CONSTRUIT UNE FOIS (`useMemo`) : le moteur de l'ecran garde le port dans
   * des callbacks stables, un objet neuf a chaque rendu les recreerait tous.
   */
  const creation = useMemo<CreationPort>(
    () => ({
      create: async ({ family, type, fields }) =>
        await sdk.createIn(family, { orgId: config.orgId, type, ...fields }),
    }),
    [sdk, config.orgId],
  );

  /*
   * LE PORT D'ACCES, monte ICI pour la meme raison que les modeles : l'hote a
   * le SDK, le socle ne l'a pas. Le socle ne connait que la FORME (`AccesPort`)
   * et la REGLE (`niveauEffectif`) ; c'est l'hote qui lit la fiche membre.
   *
   * ET IL EST MONTE AUTOUR DU SHELL, DONC AVANT `AuthGuard` : tant que
   * personne n'est connecte, `pret` reste faux et RIEN n'est masque ni grise.
   * C'est exactement l'etat d'avant ce chantier, et les rules, elles, refusent
   * quand meme.
   */
  const { user, claims, rattachement } = useUser();
  const email = user?.email?.toLowerCase() ?? null;
  const photoDeMonCompte = user?.photoURL ?? null;

  /*
   * LA PORTE A DEJA DIT NON.
   *
   * Depuis que l'ecran de connexion porte un bouton Google, quelqu'un peut
   * s'authentifier sans etre invite ici : la porte (`rattacherCompte`, cote
   * serveur, interrogee par `useUser`) rend alors un verdict, et le socle a un
   * ecran pour ca (`AccesRefuseScreen`, monte par `AuthGuard`).
   *
   * L'HOTE DOIT S'EFFACER, et c'est tout l'objet de ce booleen. Sans lui, on
   * irait quand meme lire la fiche membre, les rules la refuseraient faute de
   * claim `orgId`, et la banniere ci-dessous dirait « impossible de verifier
   * tes droits » : une panne, a quelqu'un dont le cas est parfaitement connu et
   * parfaitement normal. On laisse donc passer jusqu'au refus, qui, lui, sait
   * quoi dire.
   */
  const entreeRefusee = rattachement !== null && rattachement.statut !== 'ok';
  const roleDesClaims = roleValide(claims.orgRole);

  const [fiche, setFiche] = useState<MembreVue | null>(null);
  const [pret, setPret] = useState(false);
  const [erreurMembre, setErreurMembre] = useState<string | null>(null);
  /**
   * L'org que le JETON porte quand la lecture est refusee et qu'elle differe de
   * celle de la config : c'est le seul refus qu'on sache nommer, et en local le
   * seul qu'on sache reparer d'un clic.
   */
  const [orgDuJeton, setOrgDuJeton] = useState<string | null>(null);

  /*
   * LIRE LA FICHE, ET NE PAS CROIRE LE JETON DU PREMIER COUP.
   *
   * Les rules n'acceptent la lecture que si l'org du jeton est celle du chemin,
   * donc celle de `config.orgId`. Or le jeton vit en cache une heure : si les
   * claims ont change ailleurs (un admin qui deplace un compte, en local un
   * autre onglet qui change d'org de travail), le premier refus vient d'un
   * jeton PERIME, pas d'un droit retire. On le rafraichit UNE fois et on relit ;
   * le second refus, lui, est vrai.
   *
   * Quand il est vrai, on regarde si le jeton et la config parlent de la meme
   * org : c'est le desaccord classique du poste de dev, et le message doit le
   * dire avec les deux noms, pas "impossible de verifier".
   *
   * ELLE SE RELIT, et il le faut : un owner qui modifie SA PROPRE fiche depuis
   * l'ecran des utilisateurs (se retirer une app, changer son role) doit voir
   * l'effet tout de suite. Sans ca, le shell continuerait d'afficher les droits
   * d'avant jusqu'au rechargement de la page. Une lecture DEPASSEE par une plus
   * recente (l'utilisateur a change entre-temps) ne pose rien : c'est le role
   * de `generation`.
   */
  const generationRef = useRef(0);
  const lireFiche = useCallback(async (): Promise<void> => {
    if (email === null) return;
    // La porte a refuse : il n'y a rien a lire, et le refus s'affiche ailleurs.
    if (entreeRefusee) {
      setFiche(null);
      setErreurMembre(null);
      setPret(true);
      return;
    }
    generationRef.current += 1;
    const generation = generationRef.current;
    const encoreValide = (): boolean => generation === generationRef.current;
    setPret(false);
    try {
      let membre: Awaited<ReturnType<typeof sdk.members.me>>;
      try {
        membre = await sdk.members.me();
      } catch (premierRefus: unknown) {
        if (user == null) throw premierRefus;
        await user.getIdToken(true);
        membre = await sdk.members.me();
      }
      /*
       * LA TRACE DU COMPTE, A L'ENTREE : une fiche qui ne porte pas encore
       * l'uid de ce compte le recoit, et son entree d'annuaire aussi. Sans ca,
       * un compte deja muni de claims (que `rattacherCompte` ne revoit jamais)
       * resterait « sans compte », et ce qu'il cree dirait « Compte inconnu ».
       * UN ECHEC NE BLOQUE RIEN : des rules pas encore deployees refusent cette
       * ecriture, et l'entree doit tenir quand meme.
       */
      if (membre != null && user != null && membre.userId !== user.uid && encoreValide()) {
        try {
          if (await sdk.members.tracerMonCompte()) membre = await sdk.members.me();
        } catch (cause: unknown) {
          console.warn('[taki/acces] trace du compte impossible', cause);
        }
      }
      /*
       * LA PHOTO DU COMPTE (Google) rejoint l'annuaire : c'est lui que les
       * autres lisent, eux ne voient pas `photoURL` de Firebase Auth. Une seule
       * ecriture, seulement si elle a change. UN ECHEC NE BLOQUE RIEN.
       */
      if (membre != null && user != null && encoreValide()) {
        try {
          await sdk.members.enregistrerMaPhoto(user.photoURL);
        } catch (cause: unknown) {
          console.warn('[taki/acces] photo du compte non enregistree', cause);
        }
      }
      if (!encoreValide()) return;
      setFiche(membre == null ? null : vueDeMembre(membre));
      setErreurMembre(null);
      setOrgDuJeton(null);
    } catch {
      const jeton = user == null ? null : await user.getIdTokenResult().catch(() => null);
      if (!encoreValide()) return;
      setFiche(null);
      const orgLue = typeof jeton?.claims['orgId'] === 'string' ? jeton.claims['orgId'] : null;
      const desaccord = orgLue !== null && orgLue !== config.orgId;
      setOrgDuJeton(desaccord ? orgLue : null);
      setErreurMembre(
        desaccord
          ? `Ton compte est rattache a l'organisation « ${orgLue} », mais cette page travaille dans « ${config.orgId} » (orgId de taki.config.json). Aucun acces n'est accorde tant que les deux ne concordent pas.`
          : "Impossible de verifier tes droits pour le moment. Aucun acces n'est accorde tant que cette verification echoue.",
      );
    } finally {
      if (encoreValide()) setPret(true);
    }
  }, [email, sdk, user, config.orgId, entreeRefusee]);

  /*
   * [MENU DEV] LE GESTE DE SECOURS DU POSTE DE DEV : rattacher les comptes de demo a l'org
   * de la config, puis relire. Il n'existe que sous le serveur de dev ET sur les
   * emulateurs : la greffe DEV du shell est importee ici dynamiquement, donc
   * `vite build` n'en garde rien, et sur le cloud le bouton n'apparait pas.
   */
  const surEmulateurs = config.emulators != null;
  const rattacher = useCallback(async (): Promise<void> => {
    if (!import.meta.env.DEV || !surEmulateurs || user == null) return;
    setPret(false);
    try {
      const { rattacherCompteDev } = await import('@taki/shell/dev');
      await rattacherCompteDev(config.orgId, user);
    } catch (cause: unknown) {
      setErreurMembre(cause instanceof Error ? cause.message : 'Rattachement impossible.');
      setPret(true);
      return;
    }
    await lireFiche();
  }, [surEmulateurs, user, config.orgId, lireFiche]);

  /* UNE LECTURE PAR UTILISATEUR CONNECTE (la clef est l'email), pas une par
     rendu. Tant que la lecture n'a pas abouti, les apps ne sont pas montees :
     une absence constatee et une panne de lecture ne sont jamais confondues. */
  useEffect(() => {
    /*
     * L'UTILISATEUR CHANGE : LES ANNUAIRES SONT PERIMES.
     *
     * Le magasin des annuaires est un cache de MODULE, donc il SURVIT a la
     * deconnexion : le compte suivant relirait les noms charges sous les droits
     * du precedent, sans une lecture ni un mot. Sur un poste partage, ce sont
     * les titres de fiches d'un jeu qu'on n'a plus le droit de lire. On invalide
     * ICI, la ou l'hote relit la fiche membre, parce que c'est le seul endroit
     * qui voie le changement de compte.
     */
    invaliderAnnuaires();
    if (email === null) {
      generationRef.current += 1;
      setFiche(null);
      setPret(false);
      setErreurMembre(null);
      setOrgDuJeton(null);
      return;
    }
    setErreurMembre(null);
    void lireFiche();
  }, [email, lireFiche]);

  /* LE GUICHET DES MEMBRES, branche sur `sdk.members` ET `sdk.comptes`. Le
     patch traverse champ par champ : le socle autorise `displayName: null`, le
     SDK non.

     DEUX SOURCES, ET C'EST VOULU : `sdk.members` ecrit la FICHE, donc les
     DROITS, et elle suffit a inviter quelqu'un qui entrera avec Google.
     `sdk.comptes` fabrique le COMPTE, donc de quoi entrer sans Google, et il
     passe par une Cloud Function parce qu'un navigateur ne cree pas un compte
     Firebase Auth. */
  const membres = useMemo<MembresPort>(
    () => ({
      lister: async () =>
        (await sdk.members.list()).map(vueDeMembre).sort((a, b) => a.email.localeCompare(b.email)),
      ajouter: async (entree) =>
        vueDeMembre(
          await sdk.members.create({
            orgId: config.orgId,
            email: entree.email,
            orgRole: entree.orgRole,
            ...(entree.displayName != null ? { displayName: entree.displayName } : {}),
            ...(entree.apps != null ? { apps: entree.apps } : {}),
            ...(entree.portes != null ? { portes: entree.portes } : {}),
          }),
        ),
      modifier: async (cible, patch) => {
        const maj = vueDeMembre(
          await sdk.members.update(cible, {
            ...(patch.displayName != null ? { displayName: patch.displayName } : {}),
            ...(patch.orgRole != null ? { orgRole: patch.orgRole } : {}),
            ...(patch.disabled != null ? { disabled: patch.disabled } : {}),
            ...(patch.apps != null ? { apps: patch.apps } : {}),
            ...(patch.portes != null ? { portes: patch.portes } : {}),
          }),
        );
        if (cible === email) await lireFiche();
        return maj;
      },
      /* RETIRER, EN DEUX TEMPS, ET L'ORDRE EST LE SUJET.
         1. On ARCHIVE la fiche. Ca suffit a fermer la porte : les rules ne
            donnent plus rien a une fiche archivee, tout de suite, et ca ne
            demande aucune Cloud Function. C'est donc le geste qui doit
            reussir, et il reussit toujours.
         2. On demande au serveur de retirer les claims et de couper les
            sessions deja ouvertes. Ca, seule une Function sait le faire, et
            elle peut ne pas etre deployee. Un echec ici ne remet rien en
            cause : il retarde juste d'une heure la mort d'un jeton en cache,
            et l'ecran le DIT plutot que de laisser croire a l'un ou a
            l'autre. */
      retirer: async (cible) => {
        await sdk.members.archive(cible);
        if (cible === email) await lireFiche();
        try {
          await sdk.comptes.retirer(cible);
          return { sessionsCoupees: true, raison: null };
        } catch (cause: unknown) {
          return {
            sessionsCoupees: false,
            raison: cause instanceof Error ? cause.message : String(cause),
          };
        }
      },
      /* CHANGER L'ADRESSE, ET L'ORDRE EST ENCORE LE SUJET.
         1. On verifie ce qui ferait echouer la reprise (l'ancienne fiche
            existe, la nouvelle adresse est libre) AVANT de toucher a quoi que
            ce soit : l'etape 2 ne se defait pas.
         2. On retire l'ANCIEN compte (claims, sessions), au mieux. Cette
            Function reecrit aussi l'entree d'annuaire depuis l'ancienne fiche
            (archivee) : faite APRES la reprise, elle archiverait la personne
            sous son ancienne adresse et l'effacerait des choix. Faite AVANT,
            c'est la reprise qui a le dernier mot. Un echec ici ne remet rien en
            cause : la reprise archive l'ancienne fiche, et les rules ne donnent
            plus rien a une fiche archivee.
         3. La reprise : nouvelle fiche au meme `personId`, ancienne archivee,
            annuaire a la nouvelle adresse, en un seul lot. */
      changerAdresse: async (cible, nouvelle) => {
        const propre = nouvelle.trim().toLowerCase();
        if ((await sdk.members.get(cible)) === null) {
          throw new Error(`Aucune fiche a l'adresse ${cible} : rien a changer.`);
        }
        if ((await sdk.members.get(propre)) !== null) {
          throw new Error(`L'adresse ${propre} a deja une fiche dans cette organisation.`);
        }
        let sessionsCoupees = true;
        let raison: string | null = null;
        try {
          await sdk.comptes.retirer(cible);
        } catch (cause: unknown) {
          sessionsCoupees = false;
          raison = cause instanceof Error ? cause.message : String(cause);
        }
        const membre = vueDeMembre(await sdk.members.reprendreSousUneAutreAdresse(cible, propre));
        if (cible === email) await lireFiche();
        return { membre, sessionsCoupees, raison };
      },
      motDePasse: async (cible) => {
        const resultat = await sdk.comptes.motDePasse(cible);
        if (resultat.motDePasse === null) {
          throw new Error(
            "Le serveur n'a pas rendu de mot de passe : rien a montrer, rien n'a ete pose.",
          );
        }
        if (cible === email) await lireFiche();
        return { motDePasse: resultat.motDePasse, compteCree: resultat.compteCree };
      },
    }),
    [sdk, config.orgId, email, lireFiche],
  );

  const acces = useMemo<AccesPort>(() => {
    /* La fiche telle que la REGLE du socle la lit : quatre champs, pas un de
       plus. `niveauEffectif` est la meme regle que les rules, ecrite une fois. */
    const lue: FicheMembre | null =
      fiche === null
        ? null
        : {
            orgRole: fiche.orgRole,
            disabled: fiche.disabled,
            archivedAt: fiche.archivedAt,
            deletedAt: fiche.deletedAt,
            portes: fiche.portes,
            apps: fiche.apps,
          };
    const inactive =
      lue != null && (lue.disabled === true || lue.archivedAt != null || lue.deletedAt != null);
    const indisponible = accesMembreIndisponible(email, pret, erreurMembre);
    const { role, peutGererLesMembres: gere } = autorisationDeMembre(
      lue,
      roleDesClaims,
      indisponible,
    );
    return {
      email,
      pret,
      indisponible,
      erreur: erreurMembre,
      reessayer: () => void lireFiche(),
      role,
      niveau: (famille, type) =>
        indisponible ? 'none' : niveauEffectif(lue, roleDesClaims, famille, type),
      niveauApp: (appId): NiveauAcces => {
        /* MEME ORDRE QUE `niveauEffectif`, et ce n'est pas un hasard : un
           owner garde tout meme si une app lui a ete fermee par erreur, un
           membre desactive n'a rien, et le choix par app ne tranche qu'ensuite. */
        if (indisponible) return 'none';
        if (lue === null) return niveauParDefaut(roleDesClaims);
        if (inactive) return 'none';
        if (gere) return 'write';
        return fiche?.apps[appId] ?? niveauParDefaut(role);
      },
      peutGererLesMembres: gere,
      membres,
    };
  }, [email, pret, fiche, roleDesClaims, membres, erreurMembre, lireFiche]);

  /*
   * LE PORT DES PERSONNES : l'annuaire de l'org, et la personne connectee.
   *
   * `moi` VIENT DE LA FICHE DEJA LUE, pas d'une lecture de plus : c'est la meme
   * personne, et la fiche porte son `personId`. Une fiche d'avant l'annuaire
   * (sans `personId`) n'est encore personne : le jeton de creation reste vide,
   * et la case le dit (« Il manque »), plutot que d'ecrire une adresse.
   *
   * `lister` rend TOUT l'annuaire, archives comprises : une personne retiree
   * nomme encore ses fiches du passe, c'est le selecteur qui l'ecarte.
   *
   * CONSTRUIT PAR LECTURE DE FICHE (`useMemo`) : le moteur d'ecran garde le port
   * en reference, et un objet neuf a chaque rendu relancerait ses lectures.
   */
  const personnes = useMemo<PersonnesPort>(() => {
    const moi: PersonneVue | null =
      fiche === null || fiche.personId == null || fiche.personId === ''
        ? null
        : {
            personId: fiche.personId,
            displayName: fiche.displayName ?? '',
            email: fiche.email,
            userId: fiche.userId ?? null,
            statut: fiche.userId == null || fiche.userId === '' ? 'invite' : 'actif',
            archive: fiche.disabled || fiche.archivedAt != null || fiche.deletedAt != null,
            photoURL: photoDeMonCompte ?? null,
          };
    return {
      lister: async () =>
        (await sdk.personnes.list({ archived: 'all' })).map((personne) => ({
          personId: personne.personId,
          displayName: personne.displayName,
          email: personne.email,
          userId: personne.userId,
          statut: personne.statut,
          archive: personne.archivedAt != null,
          photoURL: personne.photoURL ?? null,
        })),
      moi,
      pret,
    };
  }, [sdk, fiche, pret, photoDeMonCompte]);

  /*
   * LE PORT DE BRANDING, monte ICI pour la meme raison que les quatre autres :
   * l'hote a le SDK, le socle ne l'a pas. Son detail (le cache local, l'apercu,
   * l'observation qui demarre AVANT la connexion) vit dans `branding.ts`, pour
   * que ce fichier reste lisible.
   *
   * IL PARTAGE LE DROIT DE LA GESTION DES MEMBRES (owner ou admin actif) : les
   * rules exigent exactement le meme, et deux calculs du meme droit finiraient
   * par diverger.
   */
  const branding = useBrandingDeLHote(config, sdk, acces.peutGererLesMembres);
  /* LES APPS INSTALLEES ET L'ENVIRONNEMENT, lus dans la base de l'org
     (`installation.ts`). Installer suit le droit de la gestion des membres ;
     l'environnement, l'owner seul. Les rules exigent les memes. */
  const installation = useInstallationDeLHote(sdk, config.orgId, email, environnement, {
    peutGerer: acces.peutGererLesMembres,
    estOwner: acces.role === 'owner' && !acces.indisponible,
  });
  const iaSettings = useIaSettingsDeLHote(sdk, email, acces.role === 'owner' && !acces.indisponible, user?.uid ?? null);
  const journal = useJournalDeLHote(sdk, acces.role === 'owner' && !acces.indisponible);
  /* LE PORT DES NOTIFICATIONS, meme logique : la gestion suit le droit des
     membres (owner ou admin actif), WhatsApp ne s'allume que par l'owner. */
  const notifications = useNotificationsDeLHote(sdk, email, acces.peutGererLesMembres, acces.role === 'owner' && !acces.indisponible);
  /* Les sujets de Lia du centre de communication : un document par sujet sous
     l'uid de la personne connectee (`liaConversations.ts`). */
  const liaConversations = useLiaConversationsDeLHote(sdk, user?.uid ?? null);
  /* Canaux, directs et chatter du centre : `sdk.conversations`, `sdk.messages`, la lecture (`conversationsCentre.ts`).
     Une liste dont la porte est fermee a la personne ne s'ecoute pas : les rules la refuseraient. */
  const conversationsCentre = useConversationsCentreDeLHote(sdk, user?.uid ?? null, email, {
    canaux: acces.niveau('conversations', 'canal') !== 'none',
    directs: acces.niveau('conversations', 'direct') !== 'none',
  });

  /*
   * LE RUNTIME DES APPS DECLAREES, monte ICI pour la meme raison que les cinq
   * ports precedents : l'hote a le SDK, ni le socle ni le shell ne l'ont. Une
   * app declaree (`definirAppDeclaree`) n'ecrit pas d'ecran : c'est l'ecran
   * generique de `@taki/app-runtime` qui interprete ses fichiers, et il lit son
   * SDK ici.
   *
   * `instances` SONT LES DECLARATIONS ECRITES PAR L'ORG (`declarations.ts`,
   * document `orgs/{orgId}/apps/{appId}`), observees des la connexion : une app
   * qui en a une la monte a la place de la declaration du MODELE. Le port entier
   * (`DeclarationsProvider`) sert au shell (le nom que le client a donne a
   * l'app, le panneau qui edite l'instance) ; le runtime n'en prend que la
   * table par id, sous la forme que `resoudreDeclaration` attend.
   *
   * CONSTRUIT UNE FOIS PAR LECTURE (`useMemo`) : l'ecran declare memoise son
   * assemblage sur cette valeur, et un objet neuf a chaque rendu le
   * reconstruirait entierement.
   */
  const declarations = useDeclarationsDeLHote(sdk, email, acces.peutGererLesMembres);
  /* `compte` et `projet` rangent ce que le poste garde d'un contenu Canvas
     (brouillon, copie de secours) par compte, projet et org. */
  const uid = user?.uid ?? null;
  const runtime = useMemo(
    () => ({
      sdk,
      orgId: config.orgId,
      instances: Object.fromEntries(declarations.instances),
      compte: uid,
      projet: config.firebaseConfig.projectId,
      emulateurs: config.emulators !== undefined,
    }),
    [sdk, config.orgId, declarations.instances, uid, config.firebaseConfig.projectId, config.emulators],
  );

  return (
    <TakiAppContext.Provider value={{ config, sdk }}>
      <DeclarationsProvider value={declarations}>
        <AppRuntimeProvider value={runtime}>
          <ModelesProvider value={modeles}>
            <RecurrencesProvider value={recurrences}>
            <LiensProvider value={liens}>
              <CreationProvider value={creation}>
                <AccesProvider value={acces}>
                  <PersonnesProvider value={personnes}>
                  <BrandingProvider value={branding}>
                  <InstallationProvider value={installation}>
                  <IaSettingsProvider value={iaSettings}>
                  <JournalProvider value={journal}>
                  <NotificationsProvider value={notifications}>
                  <LiaConversationsProvider value={liaConversations}>
                  <ConversationsCentreProvider value={conversationsCentre}>
                    {entreeRefusee ? (
                      /* Le socle montre le refus : `AuthGuard`, dans le shell,
                         rend `AccesRefuseScreen` au lieu des enfants. */
                      <TakiShell config={config} apps={demoRegistry} />
                    ) : (!pret || !declarations.pret || !installation.pret) && email !== null ? (
                      <div role="status" className="px-4 py-3 text-sm text-foreground">
                        Verification de tes droits en cours...
                      </div>
                    ) : erreurMembre !== null ? (
                      <div
                        role="alert"
                        className="flex items-center justify-between gap-4 border-b border-border bg-surface px-4 py-3 text-sm text-foreground"
                      >
                        <span>{erreurMembre}</span>
                        <div className="flex shrink-0 items-center gap-2">
                          {/* [MENU DEV] LE BOUTON DE SECOURS DU POSTE DE DEV, sous les
                              deux memes verrous que `rattacher` : il ne se
                              rend que si le jeton et la config se contredisent,
                              parce que c'est le seul refus qu'il repare. */}
                          {import.meta.env.DEV && surEmulateurs && orgDuJeton !== null ? (
                            <Button variant="primary" size="sm" onClick={() => void rattacher()}>
                              {`Rattacher mon compte a « ${config.orgId} »`}
                            </Button>
                          ) : null}
                          <Button variant="secondary" size="sm" onClick={() => void lireFiche()}>
                            Reessayer
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* LE SHELL RECOIT `config` INCHANGE : il reconstruit son
                         routeur quand l'identite de la config change, donc une
                         copie repeinte le remonterait a chaque frappe de l'ecran
                         Environnement. Le branding passe par le contexte. */
                      <TakiShell config={config} apps={demoRegistry} />
                    )}
                  </ConversationsCentreProvider>
                  </LiaConversationsProvider>
                  </NotificationsProvider>
                  </JournalProvider>
                  </IaSettingsProvider>
                  </InstallationProvider>
                  </BrandingProvider>
                  </PersonnesProvider>
                </AccesProvider>
              </CreationProvider>
            </LiensProvider>
            </RecurrencesProvider>
          </ModelesProvider>
        </AppRuntimeProvider>
      </DeclarationsProvider>
    </TakiAppContext.Provider>
  );
}

/**
 * LA FICHE MEMBRE DU SDK, DANS LA FORME QUE LE SOCLE LIT (`MembreVue`).
 *
 * Le SDK rend une entite complete (champs standard compris) ; le socle ne veut
 * que les champs de `MembreVue`, et veut `null` la ou le SDK laisse `undefined`. La
 * traduction vit ICI, une seule fois : c'est le meme role que `filtrerModeles`
 * pour les modeles de fiches. Un `apps` ou un `portes` absent devient un objet
 * vide, jamais `undefined` : l'ecran des utilisateurs lit ces deux sacs sans
 * garde.
 */
function vueDeMembre(membre: {
  email: string;
  personId?: string | undefined;
  displayName?: string | undefined;
  userId?: string | undefined;
  orgRole: RoleOrg;
  disabled?: boolean | undefined;
  archivedAt?: unknown;
  deletedAt?: unknown;
  apps?: Record<string, NiveauAcces> | undefined;
  portes?: Record<string, Record<string, NiveauAcces>> | undefined;
}): MembreVue {
  return {
    email: membre.email,
    personId: membre.personId ?? null,
    displayName: membre.displayName ?? null,
    userId: membre.userId ?? null,
    orgRole: membre.orgRole,
    disabled: membre.disabled === true,
    archivedAt: membre.archivedAt ?? null,
    deletedAt: membre.deletedAt ?? null,
    apps: membre.apps ?? {},
    portes: membre.portes ?? {},
  };
}

export default App;
